/**
 * DeadlinesPage - vue globale unifiee de tous les delais et relances.
 * Tri par date (ASC/DESC) ou par projet. Filtres par alerte + statut + branche.
 * Actions inline : passer le statut, faire une relance, voir le projet.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AddDelayModal } from './components/AddDelayModal';
import { BranchFilter, BranchPageHeader, useBranchFilter } from './components/BranchUI';
import SearchBar, { matchesSearch } from './components/SearchBar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALERT_COLORS = {
  red: { bg: 'bg-red-50', border: 'border-red-300', label: 'En retard', text: 'text-red-700', dot: 'bg-red-500' },
  yellow: { bg: 'bg-amber-50', border: 'border-amber-300', label: 'Dans 7j', text: 'text-amber-700', dot: 'bg-amber-500' },
  green: { bg: 'bg-green-50', border: 'border-green-200', label: 'A venir', text: 'text-green-700', dot: 'bg-green-500' },
  gray: { bg: 'bg-gray-50', border: 'border-gray-200', label: 'Indefini', text: 'text-gray-500', dot: 'bg-gray-400' },
};

const formatDate = (iso) => {
  try { return new Date(iso).toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }); } catch { return iso; }
};

const DeadlinesPage = () => {
  const navigate = useNavigate();
  const [delays, setDelays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAlert, setFilterAlert] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const initialProjectFilter = (() => { try { return new URLSearchParams(window.location.search).get('project_id') || ''; } catch { return ''; } })();
  const [filterProject, setFilterProject] = useState(initialProjectFilter);
  const [sortBy, setSortBy] = useState('date_asc');
  const [busyId, setBusyId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editDelay, setEditDelay] = useState(null);
  const [allProjects, setAllProjects] = useState([]);
  const [user, setUser] = useState(null);
  const [branchFilter, setBranchFilter] = useBranchFilter('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    axios.get(`${API}/auth/me`).then(r => setUser(r.data)).catch(() => {});
  }, []);

  const fetchAll = () => {
    setLoading(true);
    axios.get(`${API}/delays`)
      .then(r => setDelays(r.data || []))
      .catch(() => setDelays([]))
      .finally(() => setLoading(false));
    axios.get(`${API}/projects`)
      .then(r => setAllProjects(r.data || []))
      .catch(() => {});
  };

  useEffect(() => { fetchAll(); }, []);

  const handleRelance = async (delayId, e) => {
    e?.stopPropagation();
    const note = window.prompt('Note de relance (optionnel) :');
    if (note === null) return;
    setBusyId(delayId);
    try {
      await axios.post(`${API}/delays/${delayId}/relance`, { note: note || 'Relance effectuee' });
      fetchAll();
    } catch (err) { alert('Echec relance : ' + (err.response?.data?.detail || err.message)); }
    finally { setBusyId(null); }
  };

  const handleStatusChange = async (delayId, newStatus, e) => {
    e?.stopPropagation();
    setBusyId(delayId);
    try {
      await axios.put(`${API}/delays/${delayId}`, { status: newStatus });
      fetchAll();
    } catch (err) { alert('Echec maj : ' + (err.response?.data?.detail || err.message)); }
    finally { setBusyId(null); }
  };

  const projects = useMemo(() => {
    const map = {};
    delays.forEach(d => { if (d.project_id) map[d.project_id] = d.project_name || d.project_id; });
    return Object.entries(map).map(([id, name]) => ({ id, name }));
  }, [delays]);

  const statuses = useMemo(() => {
    return [...new Set(delays.map(d => d.status).filter(Boolean))];
  }, [delays]);

  const [showCompleted, setShowCompleted] = useState(false);

  const filtered = useMemo(() => {
    let list = delays;
    // Par defaut : on masque les delais termines ("Termine"/"Recu"). Toggle pour les afficher.
    const completedStatuses = ['termine', 'recu', 'cloture', 'fait', 'done'];
    if (!showCompleted) list = list.filter(d => !completedStatuses.includes((d.status || '').toLowerCase()));
    if (filterAlert) list = list.filter(d => d.alert === filterAlert);
    if (filterStatus) list = list.filter(d => d.status === filterStatus);
    if (filterProject) list = list.filter(d => d.project_id === filterProject);
    if (user?.cross_branch_access && branchFilter !== 'all') {
      list = list.filter(d => d.branch === branchFilter);
    }
    if (search.trim()) {
      list = list.filter(d => matchesSearch(d, search, [
        'description', 'project_name', 'delay_type', 'status',
        'contact_name', 'contact_email', 'contact_phone', 'comment',
      ]));
    }
    const sorted = [...list];
    if (sortBy === 'date_asc') sorted.sort((a, b) => (a.target_date || '').localeCompare(b.target_date || ''));
    if (sortBy === 'date_desc') sorted.sort((a, b) => (b.target_date || '').localeCompare(a.target_date || ''));
    if (sortBy === 'project') sorted.sort((a, b) => (a.project_name || '').localeCompare(b.project_name || ''));
    return sorted;
  }, [delays, showCompleted, filterAlert, filterStatus, filterProject, sortBy, user, branchFilter, search]);

  const completedCount = useMemo(() => {
    const completed = ['termine', 'recu', 'cloture', 'fait', 'done'];
    return delays.filter(d => completed.includes((d.status || '').toLowerCase())).length;
  }, [delays]);

  const stats = useMemo(() => {
    const s = { total: delays.length, red: 0, yellow: 0, green: 0, gray: 0 };
    delays.forEach(d => { s[d.alert] = (s[d.alert] || 0) + 1; });
    return s;
  }, [delays]);

  return (
    <div className="p-4 lg:p-8" data-testid="deadlines-page">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <BranchPageHeader user={user} baseTitle="Délais" />
            <h1 className="text-3xl font-bold text-gray-900">Delais globaux</h1>
            <p className="text-gray-500 mt-1">Vue d'ensemble de tous les delais en cours, tries par date ou par projet.</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {user?.cross_branch_access && (
              <BranchFilter value={branchFilter} onChange={setBranchFilter} testidPrefix="delays" />
            )}
            <button onClick={() => setShowAdd(true)}
                    className="realease-btn-primary flex items-center gap-2"
                    data-testid="deadlines-add-btn">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
              Nouveau delai
            </button>
          </div>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-4 gap-3 mb-6">
          {[
            { k: 'total', label: 'Total', color: '#003E40' },
            { k: 'red', label: 'En retard', color: '#DC3545' },
            { k: 'yellow', label: 'Dans 7 jours', color: '#F59E0B' },
            { k: 'green', label: 'A venir', color: '#10B981' },
          ].map(s => (
            <button key={s.k} onClick={() => setFilterAlert(s.k === 'total' ? '' : s.k)}
                    className={`realease-card p-4 text-center transition-all ${(s.k !== 'total' && filterAlert === s.k) || (s.k === 'total' && !filterAlert) ? 'ring-2 ring-offset-2 ring-[#008A8D]' : ''}`}
                    data-testid={`deadline-stat-${s.k}`}>
              <p className="text-3xl font-bold" style={{ color: s.color }}>{stats[s.k] || 0}</p>
              <p className="text-xs text-gray-500 uppercase tracking-wide mt-1">{s.label}</p>
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="realease-card p-4 mb-6 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold text-gray-500">Projet</label>
            <select value={filterProject} onChange={e => setFilterProject(e.target.value)}
                    className="border border-gray-200 rounded-lg px-2 py-1 text-sm"
                    data-testid="filter-project">
              <option value="">Tous ({projects.length})</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold text-gray-500">Statut</label>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                    className="border border-gray-200 rounded-lg px-2 py-1 text-sm"
                    data-testid="filter-status">
              <option value="">Tous</option>
              {statuses.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <label className="text-xs font-semibold text-gray-500">Tri</label>
            <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                    className="border border-gray-200 rounded-lg px-2 py-1 text-sm"
                    data-testid="sort-by">
              <option value="date_asc">Date croissante</option>
              <option value="date_desc">Date decroissante</option>
              <option value="project">Par projet</option>
            </select>
            <button onClick={() => setShowCompleted(s => !s)}
                    className={`px-3 py-1 rounded-lg text-xs font-semibold transition-colors ${showCompleted ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                    title={showCompleted ? 'Masquer les delais termines' : 'Afficher les delais termines'}
                    data-testid="toggle-completed">
              {showCompleted ? '✓ Termines visibles' : `Termines masques (${completedCount})`}
            </button>
          </div>
        </div>

        {/* Barre de recherche */}
        <SearchBar
          value={search}
          onChange={setSearch}
          placeholder="Rechercher un delai (description, projet, contact, type)..."
          resultsCount={filtered.length}
          totalCount={delays.length}
          className="mb-6"
          testid="deadlines-search" />

        {/* List */}
        {loading && <p className="text-gray-400 italic text-center py-8">Chargement...</p>}
        {!loading && filtered.length === 0 && (
          <div className="realease-card p-12 text-center text-gray-400" data-testid="no-deadlines">
            <p>Aucun delai ne correspond aux filtres.</p>
          </div>
        )}

        <div className="space-y-2" data-testid="deadlines-list">
          {filtered.map(d => {
            const meta = ALERT_COLORS[d.alert] || ALERT_COLORS.gray;
            const isBusy = busyId === d.delay_id;
            return (
              <div key={d.delay_id}
                   className={`realease-card p-4 ${meta.bg} border-l-4 ${meta.border} hover:shadow-md transition-all`}
                   data-testid={`deadline-${d.delay_id}`}>
                <div className="flex items-center gap-4">
                  <span className={`w-3 h-3 rounded-full ${meta.dot} shrink-0`}></span>
                  <button
                    onClick={() => {
                      if (d.project_id) navigate(`/projects/${d.project_id}`);
                      else if (d.lead_id) navigate(`/qualification/${d.lead_id}`);
                    }}
                    className="flex-1 min-w-0 text-left"
                    data-testid={`deadline-open-${d.delay_id}`}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900">{d.description || d.label || d.delay_type}</span>
                      {d.project_id && d.project_name && (
                        <span className="text-xs text-gray-500">🏠 {d.project_name}</span>
                      )}
                      {!d.project_id && d.lead_id && d.project_name && (
                        <span className="text-xs text-purple-600">👤 {d.project_name}</span>
                      )}
                      {!d.project_id && !d.lead_id && (
                        <span className="text-xs text-blue-600">📝 Tâche perso</span>
                      )}
                      {d.status && d.status !== 'en_cours' && (
                        <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 bg-white rounded border border-gray-200 text-gray-600">{d.status}</span>
                      )}
                      {/* Tags équipe assignée */}
                      {Array.isArray(d.assigned_users) && d.assigned_users.length > 0 && (
                        <span className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#008A8D]/10 text-[#006D72] rounded font-semibold">
                          👥 {d.assigned_users.length} assigné(s)
                        </span>
                      )}
                    </div>
                    {d.notes && <p className="text-xs text-gray-500 mt-1 line-clamp-1">{d.notes}</p>}
                    {d.last_relance_at && (
                      <p className="text-[11px] text-blue-600 mt-1">Derniere relance : {formatDate(d.last_relance_at)}</p>
                    )}
                  </button>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-bold text-gray-900">{formatDate(d.target_date)}</p>
                    {typeof d.days_left === 'number' && (
                      <p className={`text-xs font-semibold ${meta.text}`}>
                        {d.days_left < 0 ? `${Math.abs(d.days_left)} j de retard` : d.days_left === 0 ? "Aujourd'hui" : `Dans ${d.days_left} j`}
                      </p>
                    )}
                  </div>
                </div>
                {/* Actions inline */}
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  <select
                    value={d.status || 'en_cours'}
                    onChange={(e) => handleStatusChange(d.delay_id, e.target.value, e)}
                    onClick={(e) => e.stopPropagation()}
                    disabled={isBusy}
                    className="text-xs border border-gray-200 rounded px-2 py-1 bg-white"
                    data-testid={`status-${d.delay_id}`}
                  >
                    <option value="en_attente">En attente</option>
                    <option value="en_cours">En cours</option>
                    <option value="termine">Termine</option>
                    <option value="en_retard">En retard</option>
                  </select>
                  <button
                    onClick={(e) => handleRelance(d.delay_id, e)}
                    disabled={isBusy}
                    className="text-xs px-2.5 py-1 rounded bg-[#008A8D] text-white font-semibold hover:bg-[#006B6D] disabled:opacity-50 flex items-center gap-1"
                    data-testid={`relance-${d.delay_id}`}
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                    Relancer
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setEditDelay(d); }}
                    disabled={isBusy}
                    className="text-xs px-2.5 py-1 rounded border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 disabled:opacity-50"
                    data-testid={`edit-${d.delay_id}`}
                  >
                    Modifier
                  </button>
                  {typeof d.relance_count === 'number' && d.relance_count > 0 && (
                    <span className="text-[10px] text-gray-500 italic">{d.relance_count} relance{d.relance_count > 1 ? 's' : ''}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {showAdd && (
        <AddDelayModal
          projects={allProjects}
          defaultProjectId={filterProject || undefined}
          onClose={() => setShowAdd(false)}
          onSuccess={() => { setShowAdd(false); fetchAll(); }}
        />
      )}
      {editDelay && (
        <AddDelayModal
          projects={allProjects}
          editDelay={editDelay}
          onClose={() => setEditDelay(null)}
          onSuccess={() => { setEditDelay(null); fetchAll(); }}
        />
      )}
    </div>
  );
};

export default DeadlinesPage;
